// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PinInput, CopyButton } from '@mantine/core';
import { Badge, Button, Card, Center, Code, Grid, Group, Stack, Stepper, Text, Title } from '@/components/ui';
import { IconArrowLeft, IconArrowRight, IconQrcode, IconKey, IconCheck, IconCopy, IconShieldCheck } from '@tabler/icons-react';
import { AuthLayoutWrapper } from '../components';

export default function TwoFactorSetupPage() {
  const [active, setActive] = useState(0);
  const [otp, setOtp] = useState('');

  const nextStep = () => setActive((current) => Math.min(current + 1, 2));
  const prevStep = () => setActive((current) => Math.max(current - 1, 0));

  const backupCodes = [
    'ABCD-1234', 'EFGH-5678', 'IJKL-9012',
    'MNOP-3456', 'QRST-7890', 'UVWX-1234',
  ];

  const secretKey = 'JBSWY3DPEHPK3PXP';

  return (
    <AuthLayoutWrapper variant="modern" maxWidth="sm">
      <Stack gap="lg">
        {/* Back button */}
        <Button
          component={Link}
          to="/demo/auth-pages/other"
          variant="subtle"
          size="xs"
          leftSection={<IconArrowLeft size={14} />}
          w="fit-content"
        >
          Back to Other Pages
        </Button>

        {/* Header */}
        <Stack align="center" gap="xs">
          <Badge
            leftSection={<IconShieldCheck size={12} />}
            variant="gradient"
            gradient={{ from: 'teal', to: 'cyan' }}
            size="lg"
          >
            Security Setup
          </Badge>
          <Title order={2} ta="center" mt="sm">Enable 2FA</Title>
          <Text c="dimmed" size="sm" ta="center">
            Add an extra layer of security to your account
          </Text>
        </Stack>

        {/* Stepper */}
        <Stepper active={active} size="sm">
          <Stepper.Step label="Scan QR" description="Connect app">
            <Stack gap="lg" mt="lg">
              {/* QR Code placeholder */}
              <Center>
                <Card
                  p="xl"
                  style={{
                    backgroundColor: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Stack align="center" gap="sm">
                    <Center
                      style={{
                        width: 150,
                        height: 150,
                        backgroundColor: 'var(--mantine-color-gray-1)',
                        borderRadius: 8,
                      }}
                    >
                      <IconQrcode size={100} color="var(--mantine-color-gray-6)" />
                    </Center>
                    <Text size="xs" c="dimmed">Scan with authenticator app</Text>
                  </Stack>
                </Card>
              </Center>

              {/* Manual entry */}
              <Stack gap="xs" align="center">
                <Text size="sm" c="dimmed">Can't scan? Enter this key manually:</Text>
                <Group gap="xs">
                  <Code>{secretKey}</Code>
                  <CopyButton value={secretKey}>
                    {({ copied, copy }) => (
                      <Button
                        size="xs"
                        variant="subtle"
                        onClick={copy}
                        leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                      >
                        {copied ? 'Copied' : 'Copy'}
                      </Button>
                    )}
                  </CopyButton>
                </Group>
              </Stack>
            </Stack>
          </Stepper.Step>

          <Stepper.Step label="Verify" description="Enter code">
            <Stack gap="lg" mt="lg" align="center">
              <Center
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 16,
                  backgroundColor: 'var(--mantine-color-cyan-light)',
                }}
              >
                <IconKey size={32} color="var(--mantine-color-cyan-6)" />
              </Center>

              <Stack align="center" gap="xs">
                <Text size="sm" ta="center">
                  Enter the 6-digit code from your authenticator app
                </Text>
              </Stack>

              <PinInput
                length={6}
                size="lg"
                value={otp}
                onChange={setOtp}
                type="number"
                oneTimeCode
              />
            </Stack>
          </Stepper.Step>

          <Stepper.Step label="Backup" description="Save codes">
            <Stack gap="lg" mt="lg">
              <Stack align="center" gap="xs">
                <IconCheck size={48} color="var(--mantine-color-green-6)" />
                <Title order={3} ta="center">2FA Enabled!</Title>
                <Text size="sm" c="dimmed" ta="center" maw={300}>
                  Save these backup codes. Each can only be used once if you lose access to your authenticator.
                </Text>
              </Stack>

              <Card variant="subtle" p="md">
                <Grid gutter="xs">
                  {backupCodes.map((code) => (
                    <Grid.Col key={code} span={6}>
                      <Code style={{ display: 'block', textAlign: 'center', padding: '8px' }}>
                        {code}
                      </Code>
                    </Grid.Col>
                  ))}
                </Grid>
              </Card>

              <CopyButton value={backupCodes.join('\n')}>
                {({ copied, copy }) => (
                  <Button
                    variant="light"
                    onClick={copy}
                    leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                  >
                    {copied ? 'Copied all codes!' : 'Copy all codes'}
                  </Button>
                )}
              </CopyButton>
            </Stack>
          </Stepper.Step>
        </Stepper>

        {/* Navigation */}
        <Group justify="space-between" mt="md">
          {active > 0 && active < 2 ? (
            <Button variant="outline" onClick={prevStep}>
              Back
            </Button>
          ) : (
            <div />
          )}

          {active < 2 ? (
            <Button
              onClick={nextStep}
              rightSection={<IconArrowRight size={16} />}
              variant="gradient"
              gradient={{ from: 'teal', to: 'cyan' }}
              disabled={active === 1 && otp.length !== 6}
            >
              {active === 0 ? 'Continue' : 'Verify'}
            </Button>
          ) : (
            <Button
              component={Link}
              to="/demo/auth-pages/login/modern"
              variant="gradient"
              gradient={{ from: 'teal', to: 'cyan' }}
            >
              Done
            </Button>
          )}
        </Group>
      </Stack>
    </AuthLayoutWrapper>
  );
}
